import { addDays, format, setYear } from 'date-fns'
import { pick, prop } from 'ramda'
import { CollectionFieldSchema } from 'typesense/lib/Typesense/Collection'
import z from 'zod'
import { reduceToObject } from '../helperFunctions/array'
import { getSettlementDate } from './contract'
import { formatAddress } from './generic'
import { ListingStatuses } from './listing'
import { InternalSaleListingSchema } from './saleListing'
import { UserTableItemSchema } from './user'

const typesenseListingProps: readonly [
    keyof InternalSaleListingSchema,
    ...(keyof InternalSaleListingSchema)[],
] = [
    'orgId',
    'listingId',
    'additionalDetails',
    'listingAddress',
    'advertisedPrice',
    'agents',
    //'authorityStartDate',
    'authorityPeriod',
    'hiddenPrice',
    'listingStatus',
    'propertyInfo',
    'vendors',
    'geoLocation',
    'agents',
    'agentAppointmentForm',
    //'contract',
    'contractForm',
    'sellerDisclosureForm',
    'commission',
    'statusLog',
]

export const transformListingForTypesense =
    (users: UserTableItemSchema[]) => (listing: InternalSaleListingSchema) => {
        const userNames = listing.agents
            .map(prop('userId'))
            .map(userId => {
                const user = users.find(user => user.userId == userId)
                if (!user) {
                    console.log('user not found', {
                        listingId: listing.listingId,
                        userId,
                    })
                } else {
                    return `${user?.firstName} ${user?.lastName}`
                }
            })
            .join(' / ')

        const settlementDate = getSettlementDate(listing.contract)

        return {
            id: listing.listingId,
            geoLocation: [0, 0],
            agentUserIds: listing.agents.map(prop('userId')),
            authorityEndDate: listing.authorityStartDate
                ? addDays(new Date(listing.authorityStartDate), listing.authorityPeriod! - 1).getTime()
                : undefined,
            ...pick(typesenseListingProps, listing),
            addressString: formatAddress(listing.listingAddress, 'full'),
            userNames,
            authorityStartDate: listing.authorityStartDate
                ? new Date(listing.authorityStartDate).getTime()
                : undefined,
            contract: listing.contract
                ? {
                      ...listing.contract,
                      contractDate: listing.contract.contractDate
                          ? new Date(listing.contract.contractDate).getTime()
                          : undefined,
                  }
                : undefined,
            settlementDate: settlementDate?.getTime(),
            ...ListingStatuses.map(status => ({
                [`statusLog-${status}`]: listing.statusLog[status],
            })).reduce(reduceToObject, {}),
            settlementMonthDay: settlementDate ? format(settlementDate, 'MM-dd') : undefined,
            settlementMonth: settlementDate ? format(settlementDate, 'MM') : undefined,
            settlementDate1970: settlementDate ? setYear(settlementDate, 1970).getTime() : undefined,
        }
    }

export type TypesenseListingSchema = ReturnType<ReturnType<typeof transformListingForTypesense>>

export const typesenseFields: CollectionFieldSchema[] = [
    {
        name: 'orgId',
        type: 'string',
        index: true,
    },
    {
        name: 'listingId',
        type: 'string',
    },
    {
        name: 'additionalDetails',
        type: 'object',
        optional: true,
    },
    {
        name: 'geoLocation',
        type: 'geopoint',
    },
    {
        name: 'listingAddress',
        type: 'object',
    },
    {
        name: 'addressString',
        type: 'string',
        sort: true,
    },
    {
        name: 'advertisedPrice',
        type: 'string',
        optional: true,
        sort: true,
    },
    {
        name: 'authorityStartDate',
        type: 'int64',
        optional: true,
    },
    {
        name: 'authorityEndDate',
        type: 'int64',
        optional: true,
        sort: true,
    },
    {
        name: 'hiddenPrice',
        type: 'object',
        optional: true,
    },
    {
        name: 'listingStatus',
        type: 'string',
        facet: true,
        sort: true,
    },
    {
        name: 'propertyInfo',
        type: 'object',
        optional: true,
    },
    {
        name: 'propertyInfo.beds',
        type: 'float',
        optional: true,
    },
    {
        name: 'propertyInfo.baths',
        type: 'float',
        optional: true,
    },
    {
        name: 'propertyInfo.landsize',
        type: 'float',
        optional: true,
    },
    {
        name: 'vendors',
        type: 'object[]',
        optional: true,
    },
    {
        name: 'agentUserIds',
        type: 'string[]',
        facet: true,
        //sort: true,
    },
    {
        name: 'userNames',
        type: 'string',
        sort: true,
    },
    {
        name: 'contract',
        type: 'object',
        optional: true,
    },
    {
        name: 'contract.contractDate',
        type: 'int64',
        optional: true,
        sort: true,
    },
    {
        name: 'contract.contractPrice',
        type: 'float',
        optional: true,
    },
    {
        name: 'contract.commissionOverrideValue',
        type: 'float',
        optional: true,
    },
    {
        name: 'contract.initialDeposit',
        type: 'float',
        optional: true,
    },
    {
        name: 'contract.balanceDeposit',
        type: 'float',
        optional: true,
    },
    {
        name: 'contractDate',
        type: 'int64',
        optional: true,
    },
    {
        name: 'settlementDate',
        type: 'int64',
        optional: true,
        range_index: true,
    },
    { name: 'settlementDate1970', type: 'int64', optional: true },
    {
        name: 'settlementMonthDay',
        type: 'string',
        optional: true,
    },
    {
        name: 'settlementMonth',
        type: 'string',
        optional: true,
    },
    {
        name: 'statusLog-offline',
        type: 'int64',
        optional: true,
    },
    {
        name: 'statusLog-available',
        type: 'int64',
        optional: true,
    },
    {
        name: 'statusLog-under contract',
        type: 'int64',
        optional: true,
    },
    {
        name: 'statusLog-unconditional',
        type: 'int64',
        optional: true,
    },
    {
        name: 'statusLog-sold',
        type: 'int64',
        optional: true,
    },
    {
        name: 'statusLog-withdrawn',
        type: 'int64',
        optional: true,
    },
    {
        name: 'marketingPaymentStatus',
        type: 'object',
        optional: true,
        fields: [{ name: 'paymentStatus', type: 'string' }],
    },
    {
        name: 'hiddenPrice.min',
        type: 'float',
        optional: true,
    },
    {
        name: 'hiddenPrice.max',
        type: 'float',
        optional: true,
    },
]

export interface Facet {
    value: string
    count: number
    highlighted: string
}

export const SaleListingSearchSchema = z.object({
    search: z.string().optional().default(''),
    limit: z.number().max(250).optional().default(250),
    page: z.number().optional().default(1),
    status: z.string().array().optional(),
    agentIds: z.string().array().optional(),
    sort: z.string().optional(),
    sortDirection: z.enum(['asc', 'desc']).optional().default('desc'),
    actionFilters: z.record(z.string().array()).optional(),
    customFilters: z.array(z.string()).optional(),
})

export type SaleListingSearchSchema = z.infer<typeof SaleListingSearchSchema>
